import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/common/components/ui";
import ContributionService from "@/services/FinEaseUser/APIs/ContributionService";

const ContributionPaymentStatusModal = ({
  amount,
  pranNumber,
  onClose,
}: {
  amount: number;
  pranNumber: string;
  onClose: () => void;
}) => {
  const [status, setStatus] = useState<"pending" | "success" | "failed">(
    "pending"
  );
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkStatus = async () => {
      const res = await ContributionService.ContributeGetPaymentStatus(
        pranNumber ?? ""
      );
      const status = res?.status?.toLowerCase();

      if (status === "success" || status === "failed") {
        setStatus(status);
      }
    };

    checkStatus();
  }, []);

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const fetchStatus = useCallback(async () => {
    try {
      const res = await ContributionService.ContributeGetPaymentStatus(
        pranNumber ?? ""
      );

      if (res?.status) {
        const newStatus = res.status as "pending" | "success" | "failed";
        setStatus(newStatus);

        if (newStatus === "success" || newStatus === "failed") {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }
    } catch (error) {
      console.error("Status fetch error", error);
    }
  }, [pranNumber]);

  const startPolling = useCallback(() => {
    pollIntervalRef.current ??= setInterval(fetchStatus, 5000);
  }, [fetchStatus]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // -------------------------------
  // Start timer + initial poll
  // -------------------------------
  useEffect(() => {
    if (status === "pending") {
      fetchStatus();
      startPolling();
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          stopPolling();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      stopPolling();
    };
  }, [fetchStatus, startPolling, stopPolling, status]);

  // 🎉 SUCCESS UI (Screenshot 1)
  if (status === "success") {
    return (
      <div className="mx-auto w-full px-10 py-12 text-center font-sans sm:w-[500px]">
        <div className="mx-auto mt-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#E4FFFB]">
          <img
            src="/assets/Checkmark Circle.svg"
            alt="success"
            className="h-[60px]"
          />
        </div>

        <h2 className="mt-4 text-center font-sans text-[25px] font-extrabold leading-[50px] text-[var(--Dark-color,#2F363F)] md:text-[40px]">
          Congratulations
        </h2>

        <p className="mt-2 text-center font-sans text-[16px] font-bold leading-[26px] text-[var(--Secondary,#707070)]">
          Payment Done
        </p>
        <p className="mt-2 text-center font-sans text-[16px] font-bold leading-[26px] text-[var(--Secondary,#707070)]">
          Transaction Completed
        </p>

        <p className="mt-4 text-center font-sans text-[16px] font-bold leading-[26px] text-[var(--Dark-color,#2F363F)]">
          Ref: {pranNumber}
        </p>

        <Button
          onClick={onClose}
          variant="secondary"
          className="m-auto mt-6 h-[50px] cursor-pointer rounded-[8px] bg-[var(--Green,#F5F5F5)] py-2 font-sans text-[18px] font-semibold text-[#707070]"
        >
          Close
        </Button>
      </div>
    );
  }

  // ❌ FAILED UI (Screenshot 2)
  if (status === "failed") {
    return (
      <div className="w-f px-10 py-12 text-center font-sans sm:w-[500px]">
        <div className="mx-auto mt-6 flex h-[110px] w-[110px] items-center justify-center rounded-full bg-[#FFDEDE]">
          <img src="/assets/error.svg" alt="failed" className="h-[60px]" />
        </div>

        <h2 className="mt-6 font-sans text-[32px] font-bold text-[#2F363F]">
          Oops!
        </h2>

        <p className="mt-2 font-sans text-[18px] font-semibold text-[#707070]">
          Payment Failed
        </p>
        <p className="mt-2 font-sans text-[18px] font-semibold text-[#707070]">
          Transaction Failed
        </p>

        <p className="mt-4 font-sans text-[18px] font-bold text-[#2F363F]">
          Ref: {pranNumber}
        </p>

        <Button
          onClick={onClose}
          className="mx-auto mt-8 h-[50px] rounded-md bg-[#F5F5F5] px-8 font-sans text-[18px] font-semibold text-[#707070]"
        >
          Close
        </Button>
      </div>
    );
  }

  // ⏳ PENDING UI
  return (
    <div className="px-10 py-12 text-center font-sans">
      <div className="mx-auto mt-6 flex h-[90px] w-[90px] items-center justify-center rounded-full bg-[#FFF7E6]">
        <img
          src="/assets/clock.png"
          alt="clock"
          className="h-[45px] w-[45px]"
        />
      </div>

      <div className="mt-6 font-sans text-[26px] font-bold text-[#1D2C56]">
        ₹{amount}
      </div>

      <p className="mt-2 font-sans text-[16px] font-semibold text-[#707070]">
        Click on the notification received on your UPI App & enter your UPI PIN
      </p>

      <div className="mt-4 font-sans text-[25px] font-bold text-[#2F363F]">
        {formatTime(timeLeft)}
      </div>

      <p className="mt-3 font-sans text-[16px] font-bold text-[#707070]">
        To check payment status, click{" "}
        <span
          onClick={fetchStatus}
          className="cursor-pointer font-sans text-[16px] font-bold text-[#66A0CD] underline"
        >
          {"Refresh"}
        </span>
      </p>

      <p className="mt-3 max-w-xl font-sans text-[16px] font-bold text-[#707070]">
        NOTE: You will be redirected to another page once your payment is
        processed. Do not close this page or press back.
      </p>
    </div>
  );
};

export default ContributionPaymentStatusModal;
